/**
 * 旅游线路相关接口（跟团游产品）
 * 对接 Home端 - 跟团游线路 模块
 *
 * 列表: GET /api/home/product-group-tour/list
 * 详情: GET /api/home/product-group-tour/detail?id=xxx
 */
import { http } from "@/http/axios";

// ==================== 类型定义 ====================

/** 分类列表项 */
export interface TourCategory {
  id: number;
  category_name: string;
  icon: string;
  sort: number;
}

/** 分类列表响应 */
export interface TourCategoriesResponse {
  code: number;
  message: string;
  data: TourCategory[];
}

/** ★ 线路列表项（对接 product-group-tour 接口） */
export interface TourListItem {
  id: number;
  product_no: string;          // 产品编号
  title: string;               // 标题
  cover_image: string;         // 封面图
  attractions?: string | null; // 景点信息（丽江古城、玉龙雪山...）
  price: string;                // 价格（字符串格式 "1299.00"）
  group_people: number;         // 成团人数
  stock: number;                // 总库存
  days: number;                 // 行程天数
  nights: number;               // 住宿晚数
  days_text: string;            // 天数文本（如"3天2晚"）
  departure_city: string;       // 出发城市
  destinations: string;         // 目的地
}

/** ★ 线路列表请求参数（对应 product-group-tour/list） */
export interface TourListParams {
  page?: number;              // 页码，默认1
  limit?: number;             // 每页数量，默认10，最大50
  keyword?: string;           // 关键词搜索（产品标题/目的地）
  departure_city?: string;    // 出发城市筛选（模糊匹配）
  days?: number | string;     // 行程天数筛选：1→1-3天, 2→4-6天, 3→7天及以上
  min_price?: number | string;// 最低价格
  max_price?: number | string;// 最高价格
  sort?: string;              // 排序：sort=默认, price_asc=价格升序, price_desc=价格降序
}

/** 线路列表响应 */
export interface TourListResponse {
  code: number;
  message: string;
  data: {
    list: TourListItem[];
    total: number;
    page: number;
    limit: number;
  };
}

/** 行程日程项 */
export interface ItineraryDay {
  day_number: number;
  accommodation: string;
  transportation: string;
  meals: string;
  attractions: string;
}

/** 评论统计 */
export interface CommentStats {
  comment_count: number;
  avg_product_score: number;
  avg_service_score: number;
}

/** 评论项 */
export interface CommentItem {
  id: number;
  uid: number;
  nickname: string;
  avatar: string;
  comment: string;
  pics: string[];
  product_score: number;
  service_score: number;
  tags: string[];
  add_time: number;
  add_time_text: string;
  is_reply: number;
  merchant_reply_content: string;
  merchant_reply_time: string | null;
}

/** 取消政策项 */
export interface CancelPolicyItem {
  days: string;
  fee: string;
}

/** ★ 线路详情数据（对接 product-group-tour/detail） */
export interface TourDetailData {
  id: number;
  product_no: string;
  title: string;
  cover_image: string;
  images?: string[];
  attractions?: string | null;       // 景点信息
  price: string;                    // 价格
  old_price?: string;               // 原价
  child_price?: string;             // 儿童价
  group_people: number;             // 成团人数
  stock: number;                    // 总库存
  available_stock?: number;         // 可售库存
  days: number;                     // 行程天数
  nights: number;                   // 住宿晚数
  days_text: string;                // 天数文本
  destinations?: string | null;     // 目的地
  departure_city: string;           // 出发城市
  gather_place?: string;            // 集合地点
  gather_time?: string | null;      // 集合时间
  fee_include?: string[];           // 费用包含
  itinerary?: ItineraryDay[];        // 行程安排
  sold_count?: number;              // 已售数量
  is_recommend?: number | null;
  is_hot?: number | null;
  mer_id?: number;                  // 商家ID
  mer_name?: string | null;         // 商家名称
  travel_type?: number;             // 旅游类型
  travel_type_text?: string;        // 旅游类型文字
}

/** 线路详情响应 */
export interface TourDetailResponse {
  code: number;
  message: string;
  data: TourDetailData;
}

/** 价格档位项 */
export interface TourPriceItem {
  id: number;
  room_type: string;             // 档次名称（如"舒适"、"豪华"、"轻奢"）
  price: string;                 // 成本价
  sale_price: string;            // 销售价
  agent_rate: string;            // 代理费率
  single_room_supplement: string; // 单房差
  single_bike_seat: string;      // 单人单车位
}

/** 出团日期段 + 档位价格 */
export interface TourPriceDateGroup {
  start_date: string;  // 出发日期
  end_date: string;    // 返回日期
  prices: TourPriceItem[];
}

/** 价格列表响应 */
export interface TourPriceListResponse {
  code: number;
  message: string;
  data: TourPriceDateGroup[];
}

// ==================== 接口请求 ====================

/**
 * 获取旅游线路分类列表
 * @param type 类型，默认tour
 */
export function getTourCategories(type: string = "tour") {
  return http.get<TourCategoriesResponse>("/home/tour/categories", {
    params: { type },
  });
}

/**
 * ★ 获取旅游线路列表（product-group-tour 接口）
 * 支持分页 + 多条件筛选
 */
export function getTourList(params: TourListParams = {}) {
  return http.get<TourListResponse>("/home/product-group-tour/list", { params });
}

/**
 * ★ 获取旅游线路详情（product-group-tour 接口）
 * @param id 产品ID（必填）
 */
export function getTourDetail(id: number) {
  return http.get<TourDetailResponse>("/home/product-group-tour/detail", {
    params: { id },
  });
}

/**
 * ★ 获取旅游线路价格列表（product-group-tour 接口）
 * 返回出团日期段 + 各档位（舒适/轻奢/豪华）价格
 * @param groupTourId 团游产品ID
 */
export function getTourPriceList(groupTourId: number) {
  return http.get<TourPriceListResponse>("/home/product-group-tour/price-list", {
    params: { group_tour_id: groupTourId },
  });
}